import { useDndMonitor, useDroppable } from '@dnd-kit/core';
import { SortableContext, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import {
  DragHandleDots2Icon,
  PlusIcon,
  StackIcon,
  TextAlignMiddleIcon,
} from '@radix-ui/react-icons';
import { AnimatePresence, motion } from 'motion/react';
import { memo, useCallback, useEffect, useMemo, useState } from 'react';

import { Button } from '../../primitives/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../../primitives/dropdown-menu';
import { Select, SelectContent, SelectItem, SelectTrigger } from '../../primitives/select';
import { cn } from '../../utils/cn';
import { ListNotionItem } from '../list/list-notion-item';
import { Condition } from './condition';
import { LOGICAL_OPERATOR_LABELS } from './constants';
import { useRuleBuilderDisabled, useRuleBuilderStore } from './store';
import type { TFact, TGroupNode, TLogicalOperator, TObjectPath } from './types';
import { canAddToGroup, getAllowedOperators, isNodeVisibleInBuilder } from './utils';

/** CSS classes for the operator badge, keyed by logical operator. */
const OPERATOR_BADGE_COLORS: Record<TLogicalOperator, string> = {
  all: 'bg-indigo-300 text-indigo-950',
  any: 'bg-amber-300 text-amber-950',
  not: 'bg-rose-300 text-rose-950',
};

const IconOperator = ({ children }: { children: React.ReactNode }) => {
  return (
    <div className="flex size-7 items-center justify-center rounded-full bg-muted/80 text-xs font-semibold dark:text-foreground">
      {children}
    </div>
  );
};

/** Icon components for each logical operator. */
const OPERATOR_ICONS: Record<TLogicalOperator, React.ElementType> = {
  all: () => <IconOperator>all</IconOperator>,
  any: () => <IconOperator>any</IconOperator>,
  not: () => <IconOperator>not</IconOperator>,
};

type TGroupProps = {
  /** The group node data to render. */
  node: TGroupNode;
  /** Available facts passed down to child condition rows. */
  facts: TFact[];
  /** ID of the parent group, used for sortable drag data. Undefined for the root group. */
  parentGroupId?: string;
  /**
   * Logical operator of the parent group.
   * Used to derive which operators are allowed for this node (e.g. prevent consecutive `not`).
   */
  parentOperator?: TLogicalOperator | null;
  /** Whether this group is the top-level root (disables drag handle and delete button). */
  isRoot?: boolean;
  /** Names of facts that were created as custom facts in this RuleBuilder instance. */
  customFactNames: Set<string>;
  /** Names of facts currently used by at least one condition in the rule tree. */
  usedFactNames: Set<string>;
  /** When true, deletion uses a dropdown confirmation step. When false, deletes immediately. */
  requireConfirmation?: boolean;
  /** Registers a custom fact in the current RuleBuilder instance so all conditions can reuse it. */
  onCreateCustomFact: (customFact: TFact) => void;
  /** Deletes a custom fact from the current RuleBuilder instance. */
  onDeleteCustomFact: (factName: string) => void;
  /** Object path catalog indexed by fact name. */
  objectPathsByFact: Record<string, TObjectPath[]>;
  /** In-use object paths indexed by fact name. */
  usedObjectPathsByFact: Record<string, Set<string>>;
  /** Registers a custom path under a specific object fact. */
  onCreateObjectPath: (factName: string, objectPath: TObjectPath) => void;
  /** Deletes a custom path under a specific object fact. */
  onDeleteObjectPath: (factName: string, path: string) => void;
};

/**
 * Variants for each sortable child wrapper (condition or group row).
 * Exit uses a dynamic function: when the group becomes empty (childrenCount === 0)
 * the last item collapses to 6 rem instead of 0 to smoothly reveal the empty
 * placeholder that occupies that same height.
 */
const childVariants = {
  enter: { opacity: 0 },
  visible: { opacity: 1 },
  exit: ({ count, isRoot }: { count: number; isRoot: boolean }) => ({
    height: !isRoot && count === 0 ? '6rem' : 0,
    opacity: 0,
    overflow: 'hidden' as const,
  }),
};

/**
 * Group
 *
 * Recursive component that renders a logical group node (`all` / `any` / `not`).
 * Each group displays:
 * - A vertical connector line with the operator badge on the left.
 * - A children container with sortable condition/group rows.
 * - Action buttons to add conditions, add nested groups, or delete the group.
 *
 * Groups can be nested arbitrarily deep and support drag-and-drop reordering
 * via `@dnd-kit/sortable`. The root group is never draggable or deletable.
 */
export const Group = memo(function Group({
  node,
  facts,
  parentGroupId,
  parentOperator = null,
  isRoot = false,
  customFactNames,
  usedFactNames,
  requireConfirmation = true,
  onCreateCustomFact,
  onDeleteCustomFact,
  objectPathsByFact,
  usedObjectPathsByFact,
  onCreateObjectPath,
  onDeleteObjectPath,
}: TGroupProps) {
  const addCondition = useRuleBuilderStore((s) => s.addCondition);
  const addGroup = useRuleBuilderStore((s) => s.addGroup);
  const deleteNode = useRuleBuilderStore((s) => s.deleteNode);
  const updateOperator = useRuleBuilderStore((s) => s.updateOperator);
  const isGroupInvalid = useRuleBuilderStore(
    (s) => s.validation.showValidation && s.validation.invalidNodeIds.has(node.id),
  );
  const hasValidationErrors = useRuleBuilderStore(
    (s) => s.validation.showValidation && s.validation.invalidNodeIds.size > 0,
  );
  const validationRunCount = useRuleBuilderStore((s) => s.validation.runCount);
  const kind = useRuleBuilderStore((s) => s.kind);
  const fixedRootOperator = useRuleBuilderStore((s) => s.fixedRootOperator);
  const disabled = useRuleBuilderDisabled();
  const isRootOperatorFixed = isRoot && fixedRootOperator !== undefined;
  const visibleChildren = useMemo(
    () => node.children.filter((child) => isNodeVisibleInBuilder(child, kind)),
    [kind, node.children],
  );
  const hasVisibleChildren = visibleChildren.length > 0;

  const allowedOperators = useMemo(
    () => getAllowedOperators(node, parentOperator),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [node.operator, node.children.length, node.children[0], parentOperator],
  );
  const canAddChildren = useMemo(() => canAddToGroup(node), [node.operator, node.children.length]);

  // Delay showing the empty placeholder until all child exit animations have
  // finished. Without this, the placeholder and the exiting child overlap for
  // 150 ms, causing the group to momentarily grow then shrink.
  const [childrenFullyExited, setChildrenFullyExited] = useState(!hasVisibleChildren);
  useEffect(() => {
    if (hasVisibleChildren) setChildrenFullyExited(false);
  }, [hasVisibleChildren]);

  // Track whether a dragged element is hovering over this group
  const [isDragOver, setIsDragOver] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [isDeletePending, setIsDeletePending] = useState(false);

  useEffect(() => {
    // Auto-expand groups on every explicit validation run that has errors.
    if (hasValidationErrors) {
      setIsCollapsed(false);
    }
  }, [hasValidationErrors, validationRunCount]);

  useDndMonitor({
    onDragOver({ over }) {
      const overData = over?.data.current as
        { parentGroupId?: string; groupId?: string } | undefined;
      setIsDragOver(overData?.groupId === node.id || overData?.parentGroupId === node.id);
    },
    onDragEnd() {
      setIsDragOver(false);
    },
    onDragCancel() {
      setIsDragOver(false);
    },
  });

  // Sortable setup for when this group is a child of another group
  const {
    attributes: sortableDragAttributes,
    listeners: sortableDragListeners,
    setNodeRef: setSortableRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: node.id,
    disabled: isRoot || disabled,
    data: { parentGroupId: parentGroupId ?? null, nodeType: 'group' },
  });

  // This group acts as a droppable container for incoming dragged items
  const { setNodeRef: setDropRef } = useDroppable({
    id: `drop-${node.id}`,
    data: { groupId: node.id },
  });

  // Restrict to Y-axis only to prevent the group from drifting horizontally when
  // the drag handle is positioned far from the element's center.
  const sortableDragStyle = isRoot
    ? {}
    : { transform: transform ? `translateY(${transform.y}px)` : undefined, transition };

  const childIds = useMemo(() => visibleChildren.map((child) => child.id), [visibleChildren]);

  const handleOperatorChange = useCallback(
    (newOperator: string) => updateOperator(node.id, newOperator as TLogicalOperator),
    [node.id, updateOperator],
  );

  const handleAddCondition = useCallback(() => addCondition(node.id), [node.id, addCondition]);
  const handleAddGroup = useCallback(() => addGroup(node.id), [node.id, addGroup]);
  const handleDeleteGroup = useCallback(() => deleteNode(node.id), [node.id, deleteNode]);
  const handleToggleCollapse = useCallback(() => setIsCollapsed((v) => !v), []);

  const OperatorIcon = OPERATOR_ICONS[node.operator];

  return (
    <div
      ref={isRoot ? undefined : setSortableRef}
      style={sortableDragStyle}
      className={cn(
        'rounded-lg select-none',
        !isRoot && isDragging && 'pointer-events-none opacity-30',
      )}
    >
      <div
        className={cn('relative flex rounded-[inherit]', {
          'py-2': !isRoot,
        })}
      >
        {/* Logical operator selector: expands to show icon on hover */}
        <div
          className={cn(
            'group relative w-6 self-stretch overflow-hidden rounded-l-lg transition-[width] duration-150',
            !isRootOperatorFixed &&
              'hover:w-[4rem] has-[:focus-visible]:w-[4rem] has-[button[data-state=open]]:w-[4rem]',
            OPERATOR_BADGE_COLORS[node.operator],
            disabled && 'pointer-events-none',
          )}
        >
          <span
            className={cn(
              'pointer-events-none absolute inset-0 flex -rotate-90 items-center justify-center text-xs font-semibold whitespace-nowrap opacity-100 transition-opacity duration-150',
              !isRootOperatorFixed &&
                'group-hover:opacity-0 group-has-[:focus-visible]:opacity-0 group-has-[button[data-state=open]]:opacity-0',
            )}
          >
            {LOGICAL_OPERATOR_LABELS[node.operator]}
          </span>
          <Select
            name={`operator-${node.id}`}
            value={node.operator}
            onValueChange={handleOperatorChange}
            disabled={disabled || isRootOperatorFixed}
          >
            <SelectTrigger
              title="Operador lógico del grupo"
              className={cn(
                'size-full rounded-none border-0 p-2 shadow-none transition-opacity duration-150',
                isRootOperatorFixed
                  ? 'opacity-0'
                  : 'opacity-0 group-hover:opacity-100 group-has-[:focus-visible]:opacity-100 data-[state=open]:opacity-100',
              )}
            >
              {!disabled && !isRootOperatorFixed && <OperatorIcon />}
            </SelectTrigger>
            <SelectContent side="right" align="center">
              {allowedOperators.map((op) => {
                const Icon = OPERATOR_ICONS[op as TLogicalOperator];
                return (
                  <SelectItem key={op} value={op}>
                    <span className="flex items-center gap-3">
                      <Icon />
                      {LOGICAL_OPERATOR_LABELS[op]}
                    </span>
                  </SelectItem>
                );
              })}
            </SelectContent>
          </Select>
        </div>

        {/* Children container */}
        <motion.div
          ref={setDropRef}
          animate={{ height: isCollapsed ? '6rem' : 'auto' }}
          initial={false}
          transition={{ duration: 0.2, ease: 'easeInOut' }}
          className={cn(
            'relative flex-1 rounded-br-lg border border-l-0 border-input bg-card transition-colors',
            (isGroupInvalid || isDeletePending) &&
              'border-dashed border-destructive bg-destructive/5',
            isGroupInvalid && 'bg-destructive/5',
            isDragOver && !isDragging && !isGroupInvalid && 'border-primary bg-primary/10',
            isRoot && 'm-0 rounded-r-lg border-transparent',
          )}
        >
          <motion.div
            animate={{ opacity: isCollapsed ? 0 : 1 }}
            initial={false}
            transition={{ duration: 0.2, ease: 'easeInOut' }}
            className={cn('flex flex-col gap-2 p-2', {
              'pointer-events-none': isCollapsed,
              'min-h-[5.6rem] justify-center': isRoot && visibleChildren.length === 0,
            })}
          >
            <SortableContext items={childIds} strategy={verticalListSortingStrategy}>
              <AnimatePresence
                initial={false}
                custom={{ count: visibleChildren.length, isRoot }}
                onExitComplete={() => {
                  if (visibleChildren.length === 0) setChildrenFullyExited(true);
                }}
              >
                {visibleChildren.map((child) => (
                  <motion.div
                    key={child.id}
                    variants={childVariants}
                    initial="enter"
                    animate="visible"
                    exit="exit"
                    transition={{ duration: 0.2, ease: 'easeInOut' }}
                  >
                    {child.type === 'condition' ? (
                      <Condition
                        node={child}
                        facts={facts}
                        customFactNames={customFactNames}
                        parentGroupId={node.id}
                        usedFactNames={usedFactNames}
                        onDeleteCustomFact={onDeleteCustomFact}
                        objectPaths={objectPathsByFact[child.fact] ?? []}
                        usedObjectPaths={usedObjectPathsByFact[child.fact] ?? new Set<string>()}
                        onCreateObjectPath={onCreateObjectPath}
                        onDeleteObjectPath={onDeleteObjectPath}
                        requireConfirmation={requireConfirmation}
                        onCreateCustomFact={onCreateCustomFact}
                      />
                    ) : (
                      <Group
                        node={child}
                        facts={facts}
                        parentGroupId={node.id}
                        parentOperator={node.operator}
                        customFactNames={customFactNames}
                        usedFactNames={usedFactNames}
                        onDeleteCustomFact={onDeleteCustomFact}
                        objectPathsByFact={objectPathsByFact}
                        usedObjectPathsByFact={usedObjectPathsByFact}
                        onCreateObjectPath={onCreateObjectPath}
                        onDeleteObjectPath={onDeleteObjectPath}
                        requireConfirmation={requireConfirmation}
                        onCreateCustomFact={onCreateCustomFact}
                      />
                    )}
                  </motion.div>
                ))}
              </AnimatePresence>
            </SortableContext>

            {!isRoot && visibleChildren.length === 0 && childrenFullyExited && !isGroupInvalid && (
              <div className="flex h-[6rem] items-center justify-center rounded-lg border border-dashed py-3 text-xs text-muted-foreground">
                Suelta una condición o grupo aquí
              </div>
            )}

            {isGroupInvalid && visibleChildren.length === 0 && childrenFullyExited && (
              <p className="flex h-[6rem] items-center justify-center rounded-lg border border-dashed py-3 text-xs text-destructive">
                El grupo no puede estar vacío
              </p>
            )}

            {/* Action buttons */}
            <div
              className={cn('flex items-center justify-between gap-4', {
                'mt-1': visibleChildren.length > 0,
              })}
            >
              <div className="flex w-full justify-between gap-2">
                {canAddChildren && (
                  <Button
                    aria-label="Agregar condición"
                    className="opacity-80"
                    disabled={disabled}
                    size="icon"
                    title="Agregar condición"
                    variant="secondary"
                    onClick={handleAddCondition}
                  >
                    <PlusIcon className="size-4" />
                  </Button>
                )}
                {canAddChildren && (
                  <Button
                    aria-label="Agregar grupo"
                    className="opacity-80"
                    disabled={disabled}
                    size="icon"
                    title="Agregar grupo"
                    variant="secondary"
                    onClick={handleAddGroup}
                  >
                    <StackIcon className="size-4" />
                  </Button>
                )}
              </div>
              {!isRoot &&
                (requireConfirmation ? (
                  <DropdownMenu open={isDeletePending} onOpenChange={setIsDeletePending}>
                    <DropdownMenuTrigger asChild disabled={disabled}>
                      <Button
                        aria-label="Eliminar grupo"
                        className="opacity-50"
                        title="Eliminar grupo"
                        variant="secondary"
                      >
                        Eliminar grupo
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent className="w-[9rem]" side="left" align="center">
                      <DropdownMenuItem onClick={handleDeleteGroup}>
                        <ListNotionItem
                          className="cursor-pointer text-destructive"
                          description="Clic para confirmar"
                          title="Eliminar grupo"
                          truncate={false}
                        />
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                ) : (
                  <Button
                    aria-label="Eliminar grupo"
                    className="opacity-50"
                    disabled={disabled}
                    title="Eliminar grupo"
                    variant="secondary"
                    onClick={handleDeleteGroup}
                  >
                    Eliminar grupo
                  </Button>
                ))}
            </div>
          </motion.div>

          <motion.div
            animate={{ opacity: isCollapsed ? 1 : 0 }}
            initial={false}
            transition={{ duration: 0.2, ease: 'easeInOut' }}
            className="pointer-events-none absolute inset-0 flex items-center justify-center"
          >
            <span className="text-xs text-primary">Grupo colapsado</span>
          </motion.div>
        </motion.div>

        {/* Drag handle and collapse toggle for non-root groups */}
        {!isRoot && (
          <div className="flex flex-col gap-1">
            {!disabled && (
              <Button
                aria-label="Arrastrar grupo"
                className="cursor-grab touch-none rounded-l-none rounded-r-lg border-l-0"
                size="icon"
                title="Arrastrar grupo"
                variant="outline"
                {...sortableDragAttributes}
                {...sortableDragListeners}
              >
                <DragHandleDots2Icon className="size-4" />
              </Button>
            )}
            <Button
              aria-label={isCollapsed ? 'Expandir grupo' : 'Colapsar grupo'}
              className="rounded-l-none rounded-r-lg border-l-0"
              size="icon"
              title={isCollapsed ? 'Expandir grupo' : 'Colapsar grupo'}
              variant="outline"
              onClick={handleToggleCollapse}
            >
              <TextAlignMiddleIcon className="size-4" />
            </Button>
          </div>
        )}
      </div>
    </div>
  );
});
